import type { FormatCatalog, ValidationClient } from "@businessone-io/einvoice-processor";
import { inject, Injectable, InjectionToken } from "@angular/core";
import {
    EInvoiceExportResult,
    EInvoiceFormatDefinition,
    EInvoiceFormatMatch,
    EInvoiceImportResult,
    EInvoiceValidationResult,
    EinvoiceProcessingEngine
} from "../engine/einvoice-processing-engine";

export interface ModelBinding {
    modelId: string;
    modelName: string;
}

export interface SyntaxBinding {
    syntax: string;
    modelBindings: ModelBinding[];
}

export interface Format {
    id: string;
    name: string;
    syntaxBindings: SyntaxBinding[];
}

export interface FormatMatchResult {
    id: string
    name: string;
    syntax: string;
    version: string;
    status: "released" | "preview" | "deprecated";
}

export interface ProcessorConfig {
    formatsPath?: string;
}

export const PROCESSOR_CONFIG = new InjectionToken<ProcessorConfig>('ProcessorConfig');

@Injectable({
    providedIn: 'root',
})
export class Processor implements EinvoiceProcessingEngine {

    private client: ValidationClient | null = null;
    private catalog: FormatCatalog | null = null;
    private catalogPath: string;
    private initializePromise: Promise<void> | null = null;

    constructor() {
        const config = inject(PROCESSOR_CONFIG, { optional: true });
        this.catalogPath = config?.formatsPath ?? "assets/formats";
    }

    public async initialize(): Promise<void> {
        if (this.initializePromise) {
            return this.initializePromise;
        }

        this.initializePromise = this._initialize();
        return this.initializePromise;
    }

    public async init(): Promise<void> {
        await this.initialize();
    }

    private async _initialize(): Promise<void> {
        const module = await import(/* webpackChunkName: "processor" */ "@businessone-io/einvoice-processor");
        this.client = new module.ValidationClient();

        this.client.packageResolver = async (name: string) => {
            const response = await fetch(`${this.catalogPath}/${name}`);
            return await response.arrayBuffer();
        }
        this.catalog = await (await fetch(`${this.catalogPath}/catalog.json`)).json();
        await this.client.initialize([{
            ...this.catalog!,
            baseUri: "http://"
        }]);
    }

    public async validate(xml: string, options: {
        formatId?: string;
        syntax?: string;
        version?: string;
    }): Promise<EInvoiceValidationResult> {
        await this.ensureInitialized();

        const result = await this.client!.validateXml(xml, {
            formatId: options.formatId,
            syntax: options.syntax,
            version: options.version
        });

        return {
            valid: result.valid,
            errors: result.errors ?? [],
            stepResults: result.stepResults ?? []
        };
    }

    public async probe(xml: string, options: {
        formatId?: string;
        syntax?: string;
        version?: string;
    }): Promise<EInvoiceFormatMatch[]> {
        await this.ensureInitialized();

        return (await this.client!.probeFormats(xml, {
            formatId: options.formatId,
            syntax: options.syntax

        })).map((f) => (
            {
                id: f.id,
                name: f.name,
                syntax: f.syntax,
                version: f.version,
                status: f.status,
                documentType: f.documentType,
                modelBindings: f.modelBindings
            }
        ));
    }

    public async importToModel(xml: string, formatId: string, version: string, syntax: string, modelId: string, trackSource = true): Promise<EInvoiceImportResult> {
        await this.ensureInitialized();

        const result = await this.client!.bindToModel(xml, formatId, version, syntax, modelId, trackSource);
        return {
            success: result.success,
            model: result.model,
            errors: result.errors ?? [],
            transformationErrors: result.transformationErrors ?? [],
            modelValidationErrors: result.modelValidationErrors ?? []
        };
    }

    public async bindToModel(xml: string, formatId: string, version: string, syntax: string, modelId: string, trackSource = true): Promise<EInvoiceImportResult> {
        return await this.importToModel(xml, formatId, version, syntax, modelId, trackSource);
    }

    public async exportFromModel(model: unknown, formatId: string, version: string, syntax: string, modelId: string): Promise<EInvoiceExportResult> {
        await this.ensureInitialized();

        const result = await this.client!.bindFromModel(model, formatId, version, syntax, modelId);
        return {
            success: result.success,
            document: result.document == null ? undefined : new TextEncoder().encode(result.document),
            errors: result.errors ?? [],
            transformationErrors: result.transformationErrors ?? [],
            modelValidationErrors: result.modelValidationErrors ?? []
        };
    }

    public async bindFromModel(model: unknown, formatId: string, version: string, syntax: string, modelId: string): Promise<EInvoiceExportResult> {
        return await this.exportFromModel(model, formatId, version, syntax, modelId);
    }

    public async getModelSchema(formatId: string, version: string, syntax: string, modelId: string): Promise<string | null> {
        await this.ensureInitialized();

        const result = await this.client!.getModelSchema(formatId, version, syntax, modelId) as unknown;
        if (result == null) {
            return null;
        }

        if (typeof result === 'string') {
            return result;
        }

        if (typeof result === 'object' && 'data' in result && typeof result.data === 'string') {
            return result.data;
        }

        return null;
    }

    private async ensureInitialized(): Promise<void> {
        if (!this.initializePromise) {
            await this.initialize();
        } else {
            await this.initializePromise;
        }
    }

    public async getFormats(): Promise<EInvoiceFormatDefinition[]> {
        await this.ensureInitialized();

        if (this.catalog == null) {
            return [];
        }
        const result: EInvoiceFormatDefinition[] = [];
        for (const format of this.catalog.formats) {
            result.push({
                id: format.id,
                name: format.name,
                version: format.version,
                status: format.status,
                documentType: format.documentType,
                syntaxBindings: format.syntaxProbes.map((s) => ({
                    syntax: s.syntax,
                    modelBindings: s.modelBindings.map((m) => ({
                        modelId: m.modelId,
                        modelName: m.modelName
                    }))
                }))
            })
        };
        return result;
    }
}

export { Processor as LocalProcessorEngine };
